import { useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import axiosManager from "services/axiosManager";
import { notifyError } from "services/toastService";

const GRAPH_EMAIL_URL = "https://graph.microsoft.com/v1.0/me/sendMail";
const GRAPH_EMAIL_SCOPE = "Mail.Send";

const getGraphSendMailErrorMessage = (error) => {
    const errorCode = error?.response?.data?.error?.code;

    if (errorCode === "MailboxNotEnabledForRESTAPI") {
        return "Your account does not have an Exchange Online mailbox enabled for Microsoft Graph email. Please contact IT support.";
    }

    return null;
};

export default function useEmailer() {
    const { instance: msalInstance } = useMsal();

    const createEmail = async ({ accessToken, recipients, subject, body }) => {
        const toRecipients = recipients
            .map((recipient) => {
                if (typeof recipient === "string") {
                    return recipient.trim();
                }

                return recipient?.emailAddress?.address?.trim?.() || "";
            })
            .filter(Boolean)
            .map((address) => ({
                emailAddress: { address },
            }));

        if (!toRecipients.length) {
            throw new Error("At least one recipient is required");
        }

        const response = await axiosManager.post$(
            GRAPH_EMAIL_URL,
            {
                message: {
                    subject,
                    body: {
                        contentType: "Text",
                        content: body,
                    },
                    toRecipients,
                },
                saveToSentItems: true,
            },
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            },
        );

        return response.data;
    };

    const {
        mutate,
        isPending,
        isSuccess,
        reset: resetEmail,
    } = useMutation({
        mutationFn: createEmail,
        onError: (error) => {
            const message = getGraphSendMailErrorMessage(error);

            if (message) {
                notifyError(error, { message });
                return;
            }

            notifyError(error);
        },
    });

    const onEmail = useCallback(
        ({ recipients, subject, body }) => {
            const activeAccount = msalInstance.getActiveAccount();
            const scopes = [GRAPH_EMAIL_SCOPE];

            const request = {
                account: activeAccount,
                scopes,
            };
            msalInstance
                .acquireTokenSilent(request)
                .then(({ accessToken }) => {
                    mutate({
                        accessToken,
                        recipients,
                        subject,
                        body,
                    });
                })
                .catch((error) => {
                    notifyError(error);
                });
        },
        [msalInstance, mutate],
    );

    return [onEmail, isPending, isSuccess, resetEmail];
}
